// ---------------------------------------------------------------------------
// plannerScheduleView — presentation mapping for GET /api/v2/views/schedule.
//
// The backend returns the composed Daily Schedule (scoping, matching,
// deduping and bucketing all server-side; see calliope-backend
// src/services/views/scheduleView.ts). This module ONLY does presentation
// work the client owns: card time labels in the viewer's display timezone,
// labels/accents, and the legacy raw-source stand-ins the PlannerEvent
// action handlers still consume (`item.encounter` / `item.session` /
// `item.scheduledBot` / `item.appointment`).
//
// It replaces plannerScheduleAdapter.buildPlannerSchedule +
// plannerEncounterHydration on the live schedule path. The wire now carries
// everything the old client composed locally:
//   - encounter rows carry tags (Session.tags union) → workflow/phone/video
//     signals resolve through tagsToLegacyFields exactly like the retired
//     adapter's resolveEncounterModality/isPhoneEncounter/isTelehealth trio;
//   - encounter rows carry summary + contributors → summary line and the
//     "Started by … · N contributors" detail line render again;
//   - appointment rows carry facility → detail line is provider · facility ·
//     reason (appointmentDetailLine parity);
//   - bot rows carry mode → the scheduled-bot badge distinguishes the Zoom
//     scribe bot from the telehealth interpreter default (botModality parity).
// ---------------------------------------------------------------------------

import type {
  ScheduleSectionKey,
  ScheduleViewResponse,
  ScheduleViewRow,
} from '../../services/viewsClient';
import {
  isTelehealthEncounter,
  resolveEncounterModality,
} from '../../services/encounterUtils';
import {tagsToLegacyFields} from '../../services/dataLayerClient';
import {clean} from './plannerModels';
import {isPlaceholderEncounterSummary} from './plannerEncounterMetadata';
import {formatPlannerFacilityTime} from './plannerDateTime';

const SCHEDULE_GROUP_KEYS: ScheduleSectionKey[] = [
  'completed',
  'noShows',
  'incomplete',
  'upcoming',
  'later',
];

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const titleCase = (value: any) =>
  clean(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());

const joinDetails = (...values: any[]) =>
  values.map(clean).filter(Boolean).join(' · ');

function appointmentStatusLabel(status: any, section: ScheduleSectionKey) {
  if (section === 'noShows') return 'No-show';
  const label = titleCase(status);
  return label && label !== 'Scheduled' ? label : 'Appointment';
}

const isEmailLike = (value: any) => /@/.test(clean(value));

// tagsToLegacyFields yields the V2 canonical 'telehealthScribe' casing for the
// telehealth-scribe tag; resolveEncounterModality classifies scribes by the
// legacy snake_case constants, so normalize before resolving (the retired
// adapter always fed it 'telehealth_scribe').
const normalizeWorkflowType = (workflowType: any): string => {
  const wf = clean(workflowType);
  return wf === 'telehealthScribe' ? 'telehealth_scribe' : wf;
};

/** The retired adapter's resolveEncounterModality/isPhoneEncounter/
 *  isTelehealthEncounter trio, re-derived from the row's Session tags. */
function encounterSignalsFromTags(tags: string[] | undefined) {
  const legacy = tagsToLegacyFields(tags || []);
  const workflow =
    normalizeWorkflowType(legacy.workflowType) || 'medical_scribing';
  const isTelephonic = legacy.isTelephonic;
  return {
    modality: resolveEncounterModality(workflow),
    isTelephonic,
    // Phone wins over telehealth: web dial-out encounters carry both signals
    // (telephonic tag + a telehealth workflow) but were delivered by phone.
    isTelehealth: !isTelephonic && isTelehealthEncounter(workflow),
    specialty: legacy.specialty,
  };
}

/** "Started by {contributors[0].name} · N contributors" — the schedule
 *  card's contributor detail line, from the row's resolved contributors. */
function encounterContributorLine(
  contributors: Array<{userId: string; name: string | null}>,
) {
  const starterName = clean(contributors[0]?.name);
  const startedBy =
    starterName && !isEmailLike(starterName) ? `Started by ${starterName}` : '';
  const countLabel =
    contributors.length > 1 ? `${contributors.length} contributors` : '';
  return joinDetails(startedBy, countLabel);
}

// Common envelope shared by every Daily Schedule row (same field set
// plannerScheduleAdapter.buildScheduleItem produced — plannerModels'
// plannerEventFromScheduleItem renders these untouched).
function baseItem({
  displayTimeZone,
  ...overrides
}: Record<string, any>): Record<string, any> {
  const item = {
    id: '',
    sourceType: 'unknown',
    sourceId: null,
    patientName: '',
    languageDialect: '',
    sourceLabel: '',
    modalityLabel: '',
    statusLabel: '',
    accentKey: 'default',
    detailLine: '',
    conversationSummary: '',
    isTelehealth: false,
    durationLabel: '',
    sortDate: null,
    ...overrides,
  };
  return {
    ...item,
    patientName: clean(item.patientName) || 'Unknown patient',
    time: formatPlannerFacilityTime(item.sortDate, displayTimeZone),
  };
}

function appointmentItem(
  row: ScheduleViewRow,
  section: ScheduleSectionKey,
  displayTimeZone: string,
) {
  const appt = row.appointment!;
  const sortDate = toDate(row.time.startIso);
  // Raw-source stand-in: schedulePatientIdentity reads appointment.patientId
  // for the EHR id; appointmentSummary reads appointment.reason.
  const appointment = {
    id: appt.id,
    providerId: appt.providerId,
    providerName: appt.providerName,
    status: appt.status,
    reason: appt.reason,
    facility: appt.facility ?? null,
    patientId: row.patient.ehrPatientId,
    patientName: row.patient.name,
  };
  // Demographics stand-in for buildPlannerPatient (names come from
  // patientName; dob/mrn/language ride on the row's patient block).
  const ehrPatient =
    row.patient.dob || row.patient.mrn || row.patient.language
      ? {
          dob: row.patient.dob,
          mrn: row.patient.mrn,
          language: row.patient.language,
        }
      : null;
  return baseItem({
    displayTimeZone,
    id: `${section}-appointment-${appt.id}`,
    sourceType: 'ehrAppointment',
    sourceId: appt.id || null,
    patientName: row.patient.name,
    languageDialect: clean(row.patient.language),
    sourceLabel: 'EHR',
    statusLabel: appointmentStatusLabel(appt.status, section),
    accentKey: section === 'noShows' ? 'noShow' : 'ehr',
    // appointmentDetailLine parity: provider · facility · reason.
    detailLine: joinDetails(appt.providerName, appt.facility, appt.reason),
    appointment,
    ehrPatient,
    sortDate,
  });
}

function encounterItem(
  row: ScheduleViewRow,
  section: ScheduleSectionKey,
  displayTimeZone: string,
) {
  const enc = row.encounter!;
  const startDate = toDate(row.time.startIso);
  const endDate = toDate(row.time.endIso);
  const isOpen = enc.status === 'open';
  const durationMinutes = Number(enc.totalMinutes) || 0;
  const durationLabel = durationMinutes > 0 ? `${durationMinutes} min` : '';
  const contributors =
    enc.contributors ??
    (enc.providerUserIds || []).map(userId => ({userId, name: null}));
  // Placeholder-summary filtering parity with the retired adapter — the
  // stand-in keeps the raw value (legacy EncounterView did), the card line
  // gets the filtered one.
  const rawSummary = clean(enc.summary);
  const summary = isPlaceholderEncounterSummary(rawSummary) ? '' : rawSummary;

  // Legacy EncounterView-shaped stand-in for actions.endEncounter and the
  // patient panel; carries everything the wire exposes.
  const encounter = {
    id: enc.encounterId,
    isSynthetic: false,
    patient_id: row.patient.patientId,
    ehr_patient_id: row.patient.ehrPatientId,
    patient_name: row.patient.name,
    status: enc.status,
    has_open_session: isOpen,
    conversation_ids: enc.sessionIds || [],
    contributor_user_ids: enc.providerUserIds || [],
    contributors: contributors.map(contributor => ({
      user_id: contributor.userId,
      provider_name: contributor.name || '',
    })),
    total_minutes: durationMinutes,
    summary: rawSummary,
    start_time: startDate,
    end_time: isOpen ? null : endDate,
    foreign_language: clean(row.patient.language) || '',
    _v2NoteStatus: enc.noteStatus,
  };

  // Representative member session for actions.resumeSession (the newest
  // member — the wire orders sessionIds chronologically).
  const sessionId = enc.sessionIds?.length
    ? enc.sessionIds[enc.sessionIds.length - 1]
    : null;
  const session = sessionId
    ? {
        id: sessionId,
        encounter_id: enc.encounterId,
        patient_id: row.patient.patientId,
        ehr_patient_id: row.patient.ehrPatientId,
        patient_name: row.patient.name,
        start_time: startDate,
        end_time: isOpen ? null : endDate,
        _v2SessionStatus: isOpen ? 'active' : 'ended',
      }
    : null;

  // Modality badge / phone / video signals from the row's Session tags —
  // the retired adapter's derivation, with tags standing in for the legacy
  // workflow_type/isTelephonic columns.
  const {modality, isTelephonic, isTelehealth, specialty} =
    encounterSignalsFromTags(enc.tags);

  return baseItem({
    displayTimeZone,
    id: `${section}-encounter-${enc.encounterId}`,
    sourceType: 'encounter',
    sourceId: enc.encounterId || null,
    patientName: row.patient.name,
    languageDialect: clean(row.patient.language),
    sourceLabel: modality.badge,
    modalityLabel: modality.badge,
    durationLabel,
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isTelephonic,
    isTelehealth,
    detailLine: joinDetails(
      encounterContributorLine(contributors),
      titleCase(specialty),
    ),
    conversationSummary: summary,
    encounter,
    session,
    sortDate: endDate || startDate,
    ...(section === 'incomplete' ? {elapsedMinutes: durationMinutes} : {}),
  });
}

function botItem(
  row: ScheduleViewRow,
  section: ScheduleSectionKey,
  displayTimeZone: string,
) {
  const bot = row.bot!;
  const sortDate = toDate(row.time.startIso);
  // botModality parity: mode 'scribe' means the Zoom scribe bot; any other
  // workflow string passes through; blank defaults to the telehealth
  // interpreter modality.
  const mode = clean(bot.mode);
  const modality = resolveEncounterModality(
    mode.toLowerCase() === 'scribe'
      ? 'telehealth_scribe'
      : normalizeWorkflowType(mode) || 'telehealth_interpretation',
  );
  // Raw-doc stand-in for ScheduledSessionDetailPanel / cancel / reschedule.
  const scheduledBot = {
    id: bot.id,
    meeting_url: bot.meetingUrl,
    status: bot.status,
    mode: bot.mode ?? null,
    is_scheduled: true,
    join_at: row.time.startIso,
    patient_name: row.patient.name,
    patient_id: row.patient.patientId,
    localPatientId: row.patient.patientId,
    ehrPatientId: row.patient.ehrPatientId,
    language: row.patient.language,
  };
  return baseItem({
    displayTimeZone,
    id: `${section}-scheduled-bot-${bot.id}`,
    sourceType: 'scheduledBot',
    sourceId: bot.id || null,
    patientName: row.patient.name,
    languageDialect: clean(row.patient.language),
    sourceLabel: 'Scheduled bot',
    modalityLabel: modality.badge,
    statusLabel: 'Scheduled',
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isTelehealth: true,
    detailLine: '',
    scheduledBot,
    sortDate,
  });
}

function itemForRow(
  row: ScheduleViewRow,
  section: ScheduleSectionKey,
  displayTimeZone: string,
) {
  if (row.kind === 'appointment' && row.appointment) {
    return appointmentItem(row, section, displayTimeZone);
  }
  if (row.kind === 'encounter' && row.encounter) {
    return encounterItem(row, section, displayTimeZone);
  }
  if (row.kind === 'bot' && row.bot) {
    return botItem(row, section, displayTimeZone);
  }
  return null;
}

/**
 * Server schedule sections → the {completed, noShows, incomplete, upcoming,
 * later} groups of ScheduleItem-shaped rows every planner consumer renders
 * (via plannerModels.getVisiblePlannerEventSections). Row order is preserved
 * — the server already sorted each section.
 */
export function mapScheduleViewSections(
  response: ScheduleViewResponse | null | undefined,
  {displayTimeZone}: {displayTimeZone: string},
): Record<ScheduleSectionKey, Record<string, any>[]> {
  const groups = Object.fromEntries(
    SCHEDULE_GROUP_KEYS.map(key => [key, [] as Record<string, any>[]]),
  ) as Record<ScheduleSectionKey, Record<string, any>[]>;
  if (!response?.sections) return groups;
  for (const key of SCHEDULE_GROUP_KEYS) {
    const rows = response.sections[key] || [];
    groups[key] = rows
      .map(row => itemForRow(row, key, displayTimeZone))
      .filter(Boolean) as Record<string, any>[];
  }
  return groups;
}

/**
 * Server providerScope → the scheduleScope view-model PlannerScheduleScope
 * renders. Selection state/persistence stays client-side; the hook feeds the
 * stored selection back as query params.
 */
export function mapScheduleProviderScope(
  response: ScheduleViewResponse | null | undefined,
) {
  const scope = response?.providerScope;
  const providers = scope?.providers || [];
  return {
    mode: scope?.mode || 'none',
    needsManualSelection: scope?.mode === 'needs-selection',
    autoMatched: scope?.mode === 'auto',
    availableProviders: providers,
    totalAppointments: providers.reduce(
      (sum, provider) => sum + (Number(provider.count) || 0),
      0,
    ),
  };
}
